import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Save, ArrowLeft } from 'lucide-react';
import { appsService } from '../services/api';
import toast from 'react-hot-toast';

// Validation schema
const appSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .matches(/^[a-z0-9-]+$/, 'Name can only contain lowercase letters, numbers, and hyphens'),
  host: yup.string().required('Host is required'),
  image: yup.string().required('Image is required'),
  port: yup.number().required('Port is required').min(1).max(65535),
  targetPort: yup.number().required('Target port is required').min(1).max(65535),
  replicas: yup.number().required('Replicas is required').min(1).max(10),
  sslDisabled: yup.boolean(),

  // Basic app settings
  app_env: yup.string().required('App environment is required'),
  app_debug: yup.string().required('Debug setting is required'),
  app_key: yup.string().required('App key is required'),
  app_url: yup.string().required('App URL is required'),

  // Database settings
  db_database: yup.string().required('Database name is required'),
  db_host: yup.string().required('Database host is required'),
  db_port: yup.string().required('Database port is required'),
  db_username: yup.string().required('Database username is required'),
  db_password: yup.string().required('Database password is required'),

  // S3 settings
  public_aws_bucket: yup.string().required('Public bucket name is required'),
  private_aws_bucket: yup.string().required('Private bucket name is required'),
});

const defaultConfig = {
  // Basic app settings
  APP_VERSION: "",
  APP_PORT: "8000",
  APP_ENV: "production",
  APP_DEBUG: "false",
  APP_KEY: "base64:3ilviXqB9u6DX1NRcyWGJ+sjySF+H18CPDGb3+IVwMQ=",
  APP_TIMEZONE: "UTC",
  APP_LOCALE: "en-US",
  MAX_RESULTS: "500",

  // Storage settings
  PRIVATE_FILESYSTEM_DISK: "s3_private",
  PUBLIC_FILESYSTEM_DISK: "s3_public",

  // Database settings
  DB_CONNECTION: "pgsql",
  DB_HOST: "int-tunistock-backdata-postgresql-0.int-tunistock-backdata-postgresql-hl.int-tunistock-backdata",
  DB_PORT: "5432",
  DB_USERNAME: "tunistock",
  DB_PASSWORD: "bWF4aXR1c2Vy",

  // Mail settings
  MAIL_MAILER: "smtp",
  MAIL_HOST: "mailhog",
  MAIL_PORT: "1025",
  MAIL_USERNAME: "null",
  MAIL_PASSWORD: "null",
  MAIL_TLS_VERIFY_PEER: "true",
  MAIL_FROM_ADDR: "you@example.com",
  MAIL_FROM_NAME: "TuniStock",
  MAIL_REPLYTO_ADDR: "you@example.com",
  MAIL_REPLYTO_NAME: "TuniStock",
  MAIL_AUTO_EMBED_METHOD: "attachment",

  // Protection settings
  ALLOW_BACKUP_DELETE: "false",
  ALLOW_DATA_PURGE: "false",

  // Image library
  IMAGE_LIB: "gd",

  // Backup settings
  MAIL_BACKUP_NOTIFICATION_DRIVER: "null",
  MAIL_BACKUP_NOTIFICATION_ADDRESS: "null",
  BACKUP_ENV: "true",

  // Session settings
  SESSION_LIFETIME: "12000",
  EXPIRE_ON_CLOSE: "false",
  ENCRYPT: "false",
  COOKIE_NAME: "tunistock_session",
  COOKIE_DOMAIN: "null",
  SECURE_COOKIES: "false",
  API_TOKEN_EXPIRATION_YEARS: "40",

  // Security headers
  APP_TRUSTED_PROXIES: "*",
  ALLOW_IFRAMING: "false",
  REFERRER_POLICY: "same-origin",
  ENABLE_CSP: "false",
  CORS_ALLOWED_ORIGINS: "null",
  ENABLE_HSTS: "false",

  // Cache settings
  CACHE_DRIVER: "file",
  SESSION_DRIVER: "file",
  QUEUE_DRIVER: "sync",
  CACHE_PREFIX: "tunistock",

  // Redis settings
  REDIS_HOST: "redis://int-tunistock-backdata-bitnami-redis-0.int-tunistock-backdata-bitnami-redis-headless.int-tunistock-backdata:6379,redis://int-tunistock-backdata-bitnami-redis-1.int-tunistock-backdata-bitnami-redis-headless.int-tunistock-backdata:6379,redis://int-tunistock-backdata-bitnami-redis-2.int-tunistock-backdata-bitnami-redis-headless.int-tunistock-backdata:6379",
  REDIS_PASSWORD: null,
  REDIS_CLUSTER_ENABLED: "true",
  REDIS_PORT: "6379",

  // Memcached settings
  MEMCACHED_HOST: "null",
  MEMCACHED_PORT: "null",

  // S3 settings (will be updated with specific values)
  PUBLIC_AWS_ACCESS_KEY_ID: "admin",
  PUBLIC_AWS_SECRET_ACCESS_KEY: "tunistock@2025",
  PUBLIC_AWS_DEFAULT_REGION: "default",
  PUBLIC_AWS_URL: "https://minio-api.tunistock.app",
  PUBLIC_AWS_ENDPOINT: "https://minio-api.tunistock.app",
  PUBLIC_AWS_PATH_STYLE: "true",
  PUBLIC_AWS_BUCKET_ROOT: "null",

  PRIVATE_AWS_ACCESS_KEY_ID: "admin",
  PRIVATE_AWS_SECRET_ACCESS_KEY: "tunistock@2025",
  PRIVATE_AWS_DEFAULT_REGION: "default",
  PRIVATE_AWS_URL: "https://minio-api.tunistock.app",
  PRIVATE_AWS_ENDPOINT: "https://minio-api.tunistock.app",
  PRIVATE_AWS_PATH_STYLE: "true",
  PRIVATE_AWS_BUCKET_ROOT: "null",

  // Login throttling
  LOGIN_MAX_ATTEMPTS: "5",
  LOGIN_LOCKOUT_DURATION: "60",
  RESET_PASSWORD_LINK_EXPIRES: "900",
  INVITE_PASSWORD_LINK_EXPIRES: "1500",

  // Misc settings
  LOG_CHANNEL: "stderr",
  LOG_MAX_DAYS: "10",
  APP_LOCKED: "false",
  APP_CIPHER: "AES-256-CBC",
  APP_FORCE_TLS: "false",
  GOOGLE_MAPS_API: "",
  LDAP_MEM_LIM: "500M",
  LDAP_TIME_LIM: "600"
};

function AppForm() {
  const { name } = useParams();
  const navigate = useNavigate();
  const isEditing = !!name;
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    resolver: yupResolver(appSchema),
    defaultValues: {
      sslDisabled: true,
      port: 8000,
      targetPort: 80,
      replicas: 1,
      app_env: 'production',
      app_debug: 'false',
      app_key: defaultConfig.APP_KEY,
      db_host: defaultConfig.DB_HOST,
      db_port: '5432',
      db_username: 'tunistock',
      db_password: 'bWF4aXR1c2Vy'
    }
  });

  const appName = watch('name');
  const appHost = watch('host');
  const sslDisabled = watch('sslDisabled');

  // Auto-generate related fields when name/host/ssl changes
  useEffect(() => {
    if (appName && !isEditing) {
      const protocol = sslDisabled ? 'http' : 'https';
      setValue('app_url', `${protocol}://${appHost || `${appName}.tunistock.app`}`);
      setValue('db_database', appName);
      setValue('public_aws_bucket', `${appName}-public`);
      setValue('private_aws_bucket', `${appName}-private`);
    }
  }, [appName, appHost, sslDisabled, isEditing, setValue]);

  useEffect(() => {
    if (isEditing) {
      loadApp();
    }
  }, [isEditing, name]);

  const loadApp = async () => {
    try {
      setInitialLoading(true);
      const { app } = await appsService.getOne(name);

      // Map app data to form fields
      const formData = {
        name: app.name,
        host: app.host,
        image: app.image,
        port: app.port,
        targetPort: app.targetPort,
        replicas: app.replicas,
        sslDisabled: app.sslDisabled,

        // Map config fields
        app_env: app.config.APP_ENV,
        app_debug: app.config.APP_DEBUG,
        app_key: app.config.APP_KEY,
        app_url: app.config.APP_URL,

        db_database: app.config.DB_DATABASE,
        db_host: app.config.DB_HOST,
        db_port: app.config.DB_PORT,
        db_username: app.config.DB_USERNAME,
        db_password: app.config.DB_PASSWORD,

        public_aws_bucket: app.config.PUBLIC_AWS_BUCKET,
        private_aws_bucket: app.config.PRIVATE_AWS_BUCKET
      };

      reset(formData);
    } catch (error) {
      toast.error('Failed to load application');
      console.error('Load app error:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Build the app configuration
      const appConfig = {
        name: data.name,
        host: data.host,
        sslDisabled: data.sslDisabled,
        port: data.port,
        targetPort: data.targetPort,
        image: data.image,
        replicas: data.replicas,
        config: {
          ...defaultConfig,
          APP_ENV: data.app_env,
          APP_DEBUG: data.app_debug,
          APP_KEY: data.app_key,
          APP_URL: data.app_url,

          DB_DATABASE: data.db_database,
          DB_HOST: data.db_host,
          DB_PORT: data.db_port,
          DB_USERNAME: data.db_username,
          DB_PASSWORD: data.db_password,

          PUBLIC_AWS_BUCKET: data.public_aws_bucket,
          PUBLIC_AWS_URL: `https://minio-api.tunistock.app/${data.public_aws_bucket}`,

          PRIVATE_AWS_BUCKET: data.private_aws_bucket,
          PRIVATE_AWS_URL: `https://minio-api.tunistock.app/${data.private_aws_bucket}`,
          SECURE_COOKIES: data.sslDisabled ? "false" : "true",
          APP_FORCE_TLS: data.sslDisabled ? "false" : "true"
        }
      };

      let result;
      if (isEditing) {
        result = await appsService.update(name, appConfig);
        toast.success('Application updated successfully');
      } else {
        result = await appsService.create(appConfig);
        toast.success('Application created successfully');
      }

      navigate('/apps');
    } catch (error) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} application: ${error.response?.data?.error || error.message}`);
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {isEditing ? `Edit ${name}` : 'New Application'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing ? 'Update application configuration' : 'Create a new application deployment'}
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => navigate('/apps')}
            className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Settings */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Settings</h3>
              <p className="mt-1 text-sm text-gray-500">
                Basic configuration for your application
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-1 gap-6">
                <div className="col-span-1 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="my-app"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div className="col-span-1 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Host
                  </label>
                  <input
                    {...register('host')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="my-app.tunistock.app"
                  />
                  {errors.host && (
                    <p className="mt-1 text-sm text-red-600">{errors.host.message}</p>
                  )}
                </div>

                <div className="col-span-1 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Docker Image
                  </label>
                  <input
                    {...register('image')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="taghouti/tunistock:latest"
                  />
                  {errors.image && (
                    <p className="mt-1 text-sm text-red-600">{errors.image.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Port
                    </label>
                    <input
                      {...register('port', { valueAsNumber: true })}
                      type="number"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.port && (
                      <p className="mt-1 text-sm text-red-600">{errors.port.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Target Port
                    </label>
                    <input
                      {...register('targetPort', { valueAsNumber: true })}
                      type="number"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.targetPort && (
                      <p className="mt-1 text-sm text-red-600">{errors.targetPort.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Replicas
                    </label>
                    <input
                      {...register('replicas', { valueAsNumber: true })}
                      type="number"
                      min="1"
                      max="10"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.replicas && (
                      <p className="mt-1 text-sm text-red-600">{errors.replicas.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('sslDisabled')}
                    id="sslDisabled"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sslDisabled" className="ml-2 block text-sm text-gray-900">
                    SSL Disabled
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Application Settings */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Application Settings</h3>
              <p className="mt-1 text-sm text-gray-500">
                Application-specific configuration
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Environment
                    </label>
                    <select
                      {...register('app_env')}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      disabled={watch('app_debug') === 'true'}
                    >
                      <option value="production">Production</option>
                      <option value="staging">Staging</option>
                      <option value="development">Development</option>
                      <option value="local">Local</option>
                    </select>
                    {errors.app_env && (
                      <p className="mt-1 text-sm text-red-600">{errors.app_env.message}</p>
                    )}
                  </div>

                  <div className="flex items-center mt-6">
                    <input
                      id="app_debug"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={watch('app_debug') === 'true'}
                      onChange={e => {
                        setValue('app_debug', e.target.checked ? 'true' : 'false');
                        if (e.target.checked) {
                          setValue('app_env', 'local');
                        }
                      }}
                    />
                    <label htmlFor="app_debug" className="ml-2 block text-sm text-gray-900">
                      Debug Mode
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    App URL
                  </label>
                  <input
                    {...register('app_url')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                  {errors.app_url && (
                    <p className="mt-1 text-sm text-red-600">{errors.app_url.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Database Settings */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Database Settings</h3>
              <p className="mt-1 text-sm text-gray-500">
                PostgreSQL database configuration
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Database Name
                  </label>
                  <input
                    {...register('db_database')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                  {errors.db_database && (
                    <p className="mt-1 text-sm text-red-600">{errors.db_database.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Database Host
                    </label>
                    <input
                      {...register('db_host')}
                      type="text"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.db_host && (
                      <p className="mt-1 text-sm text-red-600">{errors.db_host.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Database Port
                    </label>
                    <input
                      {...register('db_port')}
                      type="text"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.db_port && (
                      <p className="mt-1 text-sm text-red-600">{errors.db_port.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Database Username
                    </label>
                    <input
                      {...register('db_username')}
                      type="text"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.db_username && (
                      <p className="mt-1 text-sm text-red-600">{errors.db_username.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Database Password
                    </label>
                    <input
                      {...register('db_password')}
                      type="password"
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.db_password && (
                      <p className="mt-1 text-sm text-red-600">{errors.db_password.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Storage Settings */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Storage Settings</h3>
              <p className="mt-1 text-sm text-gray-500">
                MinIO S3 bucket configuration
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Public Bucket Name
                  </label>
                  <input
                    {...register('public_aws_bucket')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                  {errors.public_aws_bucket && (
                    <p className="mt-1 text-sm text-red-600">{errors.public_aws_bucket.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Private Bucket Name
                  </label>
                  <input
                    {...register('private_aws_bucket')}
                    type="text"
                    className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                  {errors.private_aws_bucket && (
                    <p className="mt-1 text-sm text-red-600">{errors.private_aws_bucket.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="loading-spinner mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? 'Update Application' : 'Create Application'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AppForm;